/**
 * CheckInSystem Component
 * Main component for the VA check-in system
 * Replaces timer-based time tracking with 30-minute interval check-ins
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { supabase } from '../../../../utils/supabase';
import shiftService from '../../../../services/shiftService';
import checkInService from '../../../../services/checkInService';
import checkInNotificationService from '../../../../services/checkInNotificationService';
import useCheckInNotifications from '../../../../hooks/useCheckInNotifications';
import Button from '../../../../components/ui/Button';
import ConfirmationDialog from '../../../../components/ui/ConfirmationDialog';
import Skeleton from '../../../../components/ui/Skeleton';
import Icon from '../../../../components/AppIcon';
import WebcamCapture from './WebcamCapture';
import CheckInForm from './CheckInForm';
import ShiftSummary from './ShiftSummary';
import CheckInNotificationBanner from './CheckInNotificationBanner';

const CheckInSystem = () => {
  const { userProfile } = useAuth();
  const notificationPollingRef = useRef(null);

  // Use check-in notifications hook only when userProfile is ready
  const { notifications, markAsRead } = useCheckInNotifications(userProfile?.id || null);  // State
  const [activeShift, setActiveShift] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Check-in flow state
  const [showWebcam, setShowWebcam] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [showCheckInForm, setShowCheckInForm] = useState(false);
  
  // Timer state
  const [timeUntilCheckin, setTimeUntilCheckin] = useState(null);
  const [isOverdue, setIsOverdue] = useState(false);
  
  // End shift confirmation dialog state
  const [showEndShiftConfirm, setShowEndShiftConfirm] = useState(false);

  // Fetch active shift and contracts on mount
  useEffect(() => {
    if (userProfile?.id) {
      fetchActiveShift();
      fetchContracts();
    }
  }, [userProfile?.id]);

  // Timer countdown effect - only update when shift is active
  useEffect(() => {
    if (!activeShift || activeShift.status !== 'active') return;

    const updateTimer = () => {
      const timeInfo = shiftService.getTimeUntilNextCheckin(activeShift);
      setTimeUntilCheckin(timeInfo);
      setIsOverdue(timeInfo.isOverdue);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [activeShift]);

  // Auto-end shift effect - ends shift if 30 minutes overdue
  useEffect(() => {
    if (!activeShift || activeShift.status !== 'active') return;

    const checkAutoEnd = async () => {
      const timeInfo = shiftService.getTimeUntilNextCheckin(activeShift);
      
      // Auto-end if overdue by 30 minutes or more
      if (timeInfo.isOverdue && timeInfo.overdueMinutes >= 30) {
        console.log(`Shift ${activeShift.id} is ${timeInfo.overdueMinutes} minutes overdue. Auto-ending...`);
        
        const { error: autoEndError } = await shiftService.autoEndOverdueShift(activeShift.id);
        
        if (autoEndError) {
          console.error('Error auto-ending shift:', autoEndError);
          setError('Failed to automatically end overdue shift');
        } else {
          setActiveShift(null);
          setError(`Your shift was automatically ended after being ${timeInfo.overdueMinutes} minutes overdue. Please remember to check in regularly.`);
          // Refresh to get updated state
          setTimeout(() => fetchActiveShift(), 1000);
        }
      }
    };

    // Check every minute for auto-end condition
    const autoEndInterval = setInterval(checkAutoEnd, 60000);
    // Also check immediately
    checkAutoEnd();

    return () => clearInterval(autoEndInterval);
  }, [activeShift]);

  // Check-in notification reminder polling effect
  useEffect(() => {
    if (!activeShift || activeShift.status !== 'active') {
      // Stop polling if no active shift
      if (notificationPollingRef.current) {
        checkInNotificationService.stopCheckInReminderPolling(notificationPollingRef.current);
        notificationPollingRef.current = null;
      }
      return;
    }

    // Start polling for check-in reminders
    console.log('Starting check-in notification polling for shift:', activeShift.id);
    notificationPollingRef.current = checkInNotificationService.startCheckInReminderPolling();

    // Cleanup on unmount or when shift ends
    return () => {
      if (notificationPollingRef.current) {
        checkInNotificationService.stopCheckInReminderPolling(notificationPollingRef.current);
        notificationPollingRef.current = null;
      }
    };
  }, [activeShift?.id, activeShift?.status]);

  // Subscribe to shift updates
  useEffect(() => {
    if (!userProfile?.id) return;

    const subscription = shiftService.subscribeToShiftUpdates(userProfile.id, (payload) => {
      if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
        setActiveShift(payload.new);
      } else if (payload.eventType === 'DELETE') {
        setActiveShift(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [userProfile?.id]);

  const fetchActiveShift = async () => {
    setIsLoading(true);
    try {
      const { data, error: fetchError } = await shiftService.getActiveShift(userProfile.id);
      if (fetchError) throw fetchError;
      setActiveShift(data);
    } catch (err) {
      console.error('Error fetching active shift:', err);
      setError('Failed to load shift status');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContracts = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('contracts')
        .select(`
          id,
          hourly_rate,
          jobs (
            id,
            title
          ),
          employer:user_profiles!employer_id (
            id,
            full_name,
            company_name
          )
        `)
        .eq('va_id', userProfile.id)
        .eq('status', 'active');

      if (fetchError) throw fetchError;
      setContracts(data || []);
      
      // Auto-select if only one contract
      if (data?.length === 1) {
        setSelectedContract(data[0].id);
      }
    } catch (err) {
      console.error('Error fetching contracts:', err);
    }
  };

  const fetchProjects = async (employerId) => {
    if (!employerId) {
      setProjects([]);
      setSelectedProject('');
      return;
    }

    setIsLoadingProjects(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('projects')
        .select('id, name, description, status')
        .eq('employer_id', employerId)
        .in('status', ['active', 'paused'])
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setProjects(data || []);
      setSelectedProject(''); // Reset selection when contract changes
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Fetch projects when contract selection changes
  useEffect(() => {
    if (selectedContract) {
      const contract = contracts.find(c => c.id === selectedContract);
      if (contract?.employer?.id) {
        fetchProjects(contract.employer.id);
      }
    } else {
      setProjects([]);
      setSelectedProject('');
    }
  }, [selectedContract, contracts]);

  const handleStartShift = async () => {
    if (!selectedContract) {
      setError('Please select a contract');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: startError } = await shiftService.startShift(
        userProfile.id,
        selectedContract,
        selectedProject || null // Pass project_id if selected, null for general work
      );

      if (startError) throw startError;

      setActiveShift(data);
      setSuccess('Shift started! Check in now to begin logging your time.');
      
      // Immediately show webcam for first check-in
      setTimeout(() => {
        setShowWebcam(true);
        setSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('Error starting shift:', err);
      setError(err.message || 'Failed to start shift');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndShift = () => {
    if (!activeShift) return;
    setShowEndShiftConfirm(true);
  };

  const handleConfirmEndShift = async () => {
    if (!activeShift) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: endError } = await shiftService.endShift(activeShift.id);
      if (endError) throw endError;

      console.log('Shift ended and timesheet entry created:', data);

      // Clear the active shift
      setActiveShift(null);
      
      // Show success message
      setSuccess('Shift ended successfully! Timesheet entry created.');
      setTimeout(() => setSuccess(null), 3000);
      
      // Close dialog
      setShowEndShiftConfirm(false);
      
      // Refetch shifts in case any cleanup is needed
      setTimeout(() => fetchActiveShift(), 500);
    } catch (err) {
      console.error('Error ending shift:', err);
      setError(err.message || 'Failed to end shift');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEndShift = () => {
    setShowEndShiftConfirm(false);
  };

  const handlePauseShift = async () => {
    if (!activeShift) return;

    setIsLoading(true);
    try {
      const { data, error: pauseError } = await shiftService.pauseShift(activeShift.id);
      if (pauseError) throw pauseError;
      setActiveShift(data);
      setSuccess('Shift paused');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error pausing shift:', err);
      setError(err.message || 'Failed to pause shift');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeShift = async () => {
    if (!activeShift) return;

    setIsLoading(true);
    try {
      const { data, error: resumeError } = await shiftService.resumeShift(activeShift.id);
      if (resumeError) throw resumeError;
      setActiveShift(data);
      setSuccess('Shift resumed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error resuming shift:', err);
      setError(err.message || 'Failed to resume shift');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckInClick = () => {
    setShowWebcam(true);
    setError(null);
  };

  const handlePhotoCaptured = (photoData) => {
    setCapturedPhoto(photoData);
    setShowWebcam(false);
    setShowCheckInForm(true);
  };

  const handleWebcamCancel = () => {
    setShowWebcam(false);
  };

  const handleCheckInSubmit = async (taskDescription, tags) => {
    if (!capturedPhoto || !activeShift) return;

    setIsLoading(true);
    setError(null);

    try {
      // Verify we have a session before uploading
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Not authenticated - please log in again');
      }

      console.log('Starting check-in process for user:', session.user.id);

      // Upload photo first
      const { url: photoUrl, storagePath, error: uploadError } = await checkInService.uploadCheckInPhoto(
        userProfile.id,
        capturedPhoto.blob
      );

      if (uploadError) throw uploadError;

      console.log('Photo uploaded successfully, creating check-in record...');

      // Create check-in record with both URL and storage path
      const { error: checkInError } = await checkInService.createCheckIn({
        shiftId: activeShift.id,
        vaId: userProfile.id,
        photoUrl,
        photoStoragePath: storagePath,
        taskDescription,
        tags
      });

      if (checkInError) throw checkInError;

      console.log('Check-in record created successfully');

      // Refresh shift data
      await fetchActiveShift();

      setSuccess('Check-in successful! Great work.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error creating check-in:', err);
      setError(err.message || 'Failed to complete check-in');
    } finally {
      setIsLoading(false);
      setShowCheckInForm(false);
      setCapturedPhoto(null);
    }
  };

  const handleCheckInFormCancel = () => {
    setShowCheckInForm(false);
    setCapturedPhoto(null);
  };

  const formatTimeRemaining = () => {
    if (!timeUntilCheckin) return '--:--';
    
    const { secondsUntilDue, isOverdue, overdueMinutes } = timeUntilCheckin;
    
    if (isOverdue) {
      return `${overdueMinutes}m overdue`;
    }
    
    const mins = Math.abs(Math.floor(secondsUntilDue / 60));
    const secs = Math.abs(secondsUntilDue % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading && !activeShift && contracts.length === 0) {
    return (
      <div className="rounded-xl shadow-md overflow-hidden border border-primary-600">
        {/* Header Skeleton */}
        <div className="bg-primary-300 dark:bg-primary-700 px-6 py-4 border-b border-gray-300">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6" />
            <Skeleton className="h-6 w-32" />
          </div>
          <Skeleton className="h-4 w-56 mt-2" />
        </div>

        <div className="p-6 space-y-6">
          {/* Contract Selection Skeleton */}
          <div>
            <Skeleton className="h-5 w-32 mb-2" />
            <Skeleton className="h-12 w-full rounded-lg" />
          </div>

          {/* Project Selection Skeleton */}
          <div>
            <Skeleton className="h-5 w-40 mb-2" />
            <Skeleton className="h-12 w-full rounded-lg" />
          </div>

          {/* Start Button Skeleton */}
          <Skeleton className="h-14 w-full rounded-lg" />

          {/* Spacer */}
          <div className="h-4" />

          {/* Additional skeleton for visual balance */}
          <div className="space-y-3">
            <Skeleton className="h-4 w-full rounded" />
            <Skeleton className="h-4 w-3/4 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className=" rounded-xl shadow-md overflow-hidden border border-primary-600">
      {/* Header */}
      <div className="bg-primary-300 dark:bg-primary-700 px-6 py-4 border-b border-gray-300 border-primary-600">
        <h2 className="text-xl font-semibold text-primary-600 dark:text-white flex items-center gap-2">
          <Icon name="Clock" className="h-6 w-6" />
          Check-In System
        </h2>
        <p className="text-gray-600 dark:text-white text-sm mt-1">
          Check in every 30 minutes to log your work time
        </p>
      </div>

      <div className="p-6">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-white border border-red-600 rounded-lg flex items-center gap-3">
            <Icon name="Alert-Circle" className="h-5 w-5 text-red-500" />
            <span className="text-red-600">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <Icon name="X" className="h-5 w-5 text-red-500" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-white border border-green-600 rounded-lg flex items-center gap-3">
            <Icon name="Check-Circle" className="h-5 w-5 text-green-500" />
            <span className="text-green-700">{success}</span>
          </div>
        )}

        {/* No Active Shift - Start Shift UI */}
        {!activeShift && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className='w-full'>
              <label className="block text-sm font-medium text-gray-200 dark:text-gray-700 mb-2">
                Select Contract
              </label>
              <select
                value={selectedContract}
                onChange={(e) => setSelectedContract(e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-gray-900 focus:ring-1 focus:ring-primary-300"
              >
                <option value="">Choose a contract...</option>
                {contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.jobs?.title || 'Untitled Job'} - {contract.employer?.company_name || contract.employer?.full_name}
                  </option>
                ))}
              </select>
            </div>

            {selectedContract && projects.length > 0 && (
              <div className='w-full'>
                <label className="block text-sm font-medium text-gray-200 dark:text-gray-700 mb-2">
                  Select Project (Optional)
                </label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  disabled={isLoadingProjects}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-gray-900 focus:ring-1 focus:ring-primary-300 disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">None - General Work</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name} {project.status === 'paused' ? '(Paused)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {contracts.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Icon name="Briefcase" className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No active contracts found.</p>
                <p className="text-sm">You need an active contract to start tracking time.</p>
              </div>
            )}
            </div>

            {contracts.length > 0 && (
              <Button
                onClick={handleStartShift}
                disabled={!selectedContract || isLoading}
                variant="primary"
                className="w-full py-4 text-lg font-semibold"
              >
                {isLoading ? (
                  <>
                    <Icon name="Spinner" className="animate-spin h-5 w-5 mr-2" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Icon name="Play" className="h-5 w-5 mr-2" />
                    Start Shift
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Active Shift UI */}
        {activeShift && (
          <div className="space-y-6">
            {/* Timer Display */}
            <div className={`text-center p-6 rounded-xl border-2 ${
              isOverdue 
                ? 'bg-gray-50 dark:bg-gray-800 border-amber-400 dark:border-amber-600' 
                : 'bg-gray-50 dark:bg-gray-800 border-slate-300 dark:border-slate-600'
            }`}>
              <p className={`text-sm font-medium mb-1 ${
                isOverdue ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'
              }`}>
                {isOverdue ? 'Check-in Overdue!' : 'Next Check-in In'}
              </p>
              <div className={`text-5xl font-bold ${
                isOverdue ? 'text-amber-600 dark:text-amber-500' : 'text-slate-700 dark:text-slate-300'
              }`}>
                {formatTimeRemaining()}
              </div>
              {isOverdue && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                  Please check in now to continue logging time
                </p>
              )}
            </div>

            {/* Check-in Button */}
            <Button
              onClick={handleCheckInClick}
              disabled={isLoading || activeShift.status === 'paused'}
              variant={isOverdue ? 'danger' : 'primary'}
              className="w-full py-4 text-lg font-semibold"
            >
              {isLoading ? (
                <>
                  <Icon name="Spinner" className="animate-spin h-5 w-5 mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="Camera" className="h-5 w-5 mr-2" />
                  Check In Now
                </>
              )}
            </Button>

            {/* Shift Summary */}
            <ShiftSummary shift={activeShift} />

            {/* Shift Controls */}
            <div className="flex gap-3">
              {activeShift.status === 'active' && (
                <Button
                  onClick={handlePauseShift}
                  disabled={isLoading}
                  variant="secondary"
                  className="flex-1"
                >
                  <Icon name="Pause" className="h-4 w-4 mr-2" />
                  Pause
                </Button>
              )}
              
              {activeShift.status === 'paused' && (
                <Button
                  onClick={handleResumeShift}
                  disabled={isLoading}
                  variant="secondary"
                  className="flex-1"
                >
                  <Icon name="Play" className="h-4 w-4 mr-2" />
                  Resume
                </Button>
              )}

              <Button
                onClick={handleEndShift}
                disabled={isLoading}
                variant="danger"
                className="flex-1"
              >
                <Icon name="Stop-Circle" className="h-4 w-4 mr-2" />
                End Shift
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* End Shift Confirmation Dialog */}
      {showEndShiftConfirm && (
        <ConfirmationDialog
          title="End Shift"
          message="Are you sure you want to end this shift? This action will create a timesheet entry."
          confirmLabel="End Shift"
          cancelLabel="Cancel"
          onConfirm={handleConfirmEndShift}
          onCancel={handleCancelEndShift}
          isLoading={isLoading}
          type="warning"
        />
      )}

      {/* Webcam Modal */}
      {showWebcam && (
        <WebcamCapture
          onCapture={handlePhotoCaptured}
          onCancel={handleWebcamCancel}
        />
      )}

      {/* Check-in Form Modal */}
      {showCheckInForm && capturedPhoto && (
        <CheckInForm
          photoPreview={capturedPhoto.dataUrl}
          onSubmit={handleCheckInSubmit}
          onCancel={handleCheckInFormCancel}
          isLoading={isLoading}
        />
      )}

      {/* Check-in Reminder Notifications */}
      <div className="fixed top-4 right-4 space-y-3 pointer-events-none z-40">
        {notifications.map((notification) => (
          <div key={notification.id} className="pointer-events-auto">
            <CheckInNotificationBanner
              notification={notification}
              onDismiss={markAsRead}
              onActionClick={() => {
                markAsRead(notification.id);
                setShowWebcam(true);
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CheckInSystem;
